import { useEffect, useState } from "react";
import TablaCotizacionesVista from "../vista/tablaCotizacionesVista";
import type { ICotizacion } from "../../../modelos/Cotizador/entidades/ICotizacion";
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type SortingState } from "@tanstack/react-table";
import { columnasTablaCotizaciones } from "../vista/constantesTablaCotizaciones";
import { estadoInicialCotizacion } from "../interfaces";
import { CotizadorModelo } from "../../../modelos/Cotizador";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { useConfirmacion } from "../../../globales/contextos/confirmacionContext";
import { ProveedorCargando, useCargando } from "../../../globales/contextos/cargandoContext";

const CotizadorCV = () => {
  const modeloCotizador = new CotizadorModelo();

  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();

  const PAGE_SIZE = 8;
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [listaCotizaciones, setListaCotizaciones] = useState<ICotizacion[]>([]);
  const [habilitarFiltros, setHabilitarFiltros] = useState<boolean>(false);
  const [filtroCotizacion, setFiltroCotizacion] = useState<ICotizacion>(estadoInicialCotizacion);
  const [seleccionadas, setSeleccionadas] = useState<ICotizacion[]>([]);

  useEffect(() => {
    consultarCotizaciones();
  }, []);

  const consultarCotizaciones = async () => {
    try {
      manejarCargando(true, "Consultando cotizaciones...", false);
      const cotizaciones = await modeloCotizador.obtenerListaCotizaciones();
      setListaCotizaciones(cotizaciones.Data.filter(c => !c.Eliminado));
      setSeleccionadas([]); // Limpiar selección al consultar
    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar cotizaciones',
        'No fue posible consultar las cotizaciones. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    }
    finally {
      manejarCargando(false);
    }
  }

  const manejarHabilitarFiltros = (habilitar: boolean) => {
    setHabilitarFiltros(habilitar)
  }

  const manejarCambioFiltroCotizacion = async (campo: keyof ICotizacion, valor: ICotizacion[keyof ICotizacion]) => {
    setFiltroCotizacion((prevFiltro) => ({
      ...prevFiltro,
      [campo]: valor
    }));
  }

  const manejarSeleccion = (cotizacion: ICotizacion) => {
    setSeleccionadas(prev => {
      const existe = prev.some(c => c.Id === cotizacion.Id);
      if (existe) {
        return prev.filter(c => c.Id !== cotizacion.Id);
      } else {
        return [...prev, cotizacion];
      }
    });
  }

  const manejarSeleccionMasiva = (seleccionarTodos: boolean) => {
    if (seleccionarTodos) {
      // Seleccionar solo las cotizaciones no aprobadas
      const pendientes = listaCotizaciones.filter(c => !c.Aprobada);
      setSeleccionadas(pendientes);
    } else {
      setSeleccionadas([]);
    }
  }

  const aprobarCotizacion = async (cotizacion: ICotizacion) => {
    const confirmado = await confirmar({
      titulo: "Aprobar cotización",
      mensaje: `¿Está seguro de que desea aprobar la cotización ${cotizacion.IdSolicitud}?`,
      textoAceptar: "Sí, aprobar",
      textoCancelar: "Cancelar",
      tipo: EnumTipoNotificacion.Success,
      opcionCancelar: true
    });

    if (confirmado) {
      try {
        manejarCargando(true);
        const respuesta = await modeloCotizador.aprobarCotizacion(cotizacion);

        if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
          consultarCotizaciones();
          mostrarNotificacion(
            EnumTipoNotificacion.Success,
            'Cotización aprobada',
            `La cotización ${cotizacion.IdSolicitud} ha sido aprobada exitosamente.`,
            3000
          );
        }

        if (respuesta.Messages.length > 0) {
          respuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              respuesta.NotificationType,
              '',
              mensaje,
              4000
            ));
        }
      }
      catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'Error al aprobar cotización',
          'No fue posible aprobar la cotización. Por favor verifica tu conexión e intenta nuevamente.',
          5000
        );
      } finally {
        manejarCargando(false);
      }
    }
  }

  const aprobarRechazarCotizaciones = async (cotizaciones: ICotizacion[], aprobar: boolean) => {
    if (cotizaciones.length === 0) {
      mostrarNotificacion(
        EnumTipoNotificacion.Warning,
        'Sin selección',
        'Por favor seleccione al menos una cotización.',
        3000
      );
      return;
    }
    if (aprobar) {
      const confirmado = await confirmar({
        titulo: "Aprobar cotizaciones masivas",
        mensaje: `¿Está seguro de que desea aprobar ${seleccionadas.length} cotización(es) seleccionada(s)?`,
        textoAceptar: "Sí, aprobar todas",
        textoCancelar: "Cancelar",
        tipo: EnumTipoNotificacion.Success,
        opcionCancelar: true
      });

      if (confirmado) {
        try {
          manejarCargando(true);
          const respuesta = await modeloCotizador.aprobarCotizacionesMasivas(seleccionadas);

          if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
            consultarCotizaciones();
            mostrarNotificacion(
              EnumTipoNotificacion.Success,
              'Cotizaciones aprobadas',
              `${seleccionadas.length} cotización(es) han sido aprobadas exitosamente.`,
              3000
            );
          }

          if (respuesta.Messages.length > 0) {
            respuesta.Messages.forEach(mensaje =>
              mostrarNotificacion(
                respuesta.NotificationType,
                '',
                mensaje,
                4000
              ));
          }
        }
        catch {
          mostrarNotificacion(
            EnumTipoNotificacion.Error,
            'Error al aprobar cotizaciones',
            'No fue posible aprobar las cotizaciones seleccionadas. Por favor verifica tu conexión e intenta nuevamente.',
            5000
          );
        } finally {
          manejarCargando(false);
        }
      }
    } else {
      const confirmado = await confirmar({
        titulo: "Rechazar cotizaciones masivas",
        mensaje: `¿Está seguro de que desea rechazar ${seleccionadas.length} cotización(es) seleccionada(s)?`,
        textoAceptar: "Sí, rechazar todas",
        textoCancelar: "Cancelar",
        tipo: EnumTipoNotificacion.Error,
        opcionCancelar: true
      });
      if (confirmado) {
        try {
          manejarCargando(true);
          const respuesta = await modeloCotizador.rechazarCotizacionesMasivas(seleccionadas);
          if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
            consultarCotizaciones();
            mostrarNotificacion(
              EnumTipoNotificacion.Success,
              'Cotizaciones rechazadas',
              `${seleccionadas.length} cotización(es) han sido rechazadas.`,
              3000
            );
          }
          if (respuesta.Messages.length > 0) {
            respuesta.Messages.forEach(mensaje =>
              mostrarNotificacion(
                respuesta.NotificationType,
                '',
                mensaje,
                4000
              ));
          }
        }
        catch {
          mostrarNotificacion(
            EnumTipoNotificacion.Error,
            'Error al rechazar cotizaciones',
            'No fue posible rechazar las cotizaciones seleccionadas. Por favor verifica tu conexión e intenta nuevamente.',
            5000
          );
        } finally {
          manejarCargando(false);
        }
      }
    }
  }

  const rechazarCotizacion = async (cotizacion: ICotizacion) => {
    const confirmado = await confirmar({
      titulo: "Rechazar cotización",
      mensaje: `¿Está seguro de que desea rechazar la cotización ${cotizacion.IdSolicitud}?`,
      textoAceptar: "Sí, rechazar",
      textoCancelar: "Cancelar",
      tipo: EnumTipoNotificacion.Error,
      opcionCancelar: true
    });

    if (confirmado) {
      try {
        manejarCargando(true);
        const respuesta = await modeloCotizador.rechazarCotizacion(cotizacion);

        if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
          consultarCotizaciones();
          mostrarNotificacion(
            EnumTipoNotificacion.Success,
            'Cotización rechazada',
            `La cotización ${cotizacion.IdSolicitud} ha sido rechazada.`,
            3000
          );
        }

        if (respuesta.Messages.length > 0) {
          respuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              respuesta.NotificationType,
              '',
              mensaje,
              4000
            ));
        }
      }
      catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'Error al rechazar cotización',
          'No fue posible rechazar la cotización. Por favor verifica tu conexión e intenta nuevamente.',
          5000
        );
      } finally {
        manejarCargando(false);
      }
    }
  }

  const tabla = useReactTable({
    data: listaCotizaciones,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex, pageSize: PAGE_SIZE }, sorting },
    onPaginationChange: updater => {
      if (typeof updater === 'function') {
        const newState = updater({ pageIndex, pageSize: PAGE_SIZE });
        setPageIndex(newState.pageIndex);
      } else {
        setPageIndex(updater.pageIndex ?? 0);
      }
    },
    pageCount: Math.ceil(listaCotizaciones.length / PAGE_SIZE),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
  });

  const todasSeleccionadas = seleccionadas.length > 0 && seleccionadas.length === listaCotizaciones.filter(c => !c.Aprobada).length;
  const algunasSeleccionadas = seleccionadas.length > 0 && seleccionadas.length < listaCotizaciones.filter(c => !c.Aprobada).length;

  const columns = columnasTablaCotizaciones({
    seleccionadas,
    manejarSeleccion,
    manejarSeleccionMasiva,
    aprobarCotizacion,
    rechazarCotizacion,
    todasSeleccionadas,
    algunasSeleccionadas
  });

  tabla.setOptions((prev) => ({ ...prev, columns }));

  return (
    <>
      <ProveedorCargando>
        <TablaCotizacionesVista
          tabla={tabla}
          consultarCotizaciones={consultarCotizaciones}
          filtro={filtroCotizacion}
          habilitarFiltros={habilitarFiltros}
          manejarCambioFiltroCotizacion={manejarCambioFiltroCotizacion}
          manejarHabilitarFiltros={manejarHabilitarFiltros}
          seleccionadas={seleccionadas}
          manejarSeleccion={manejarSeleccion}
          manejarSeleccionMasiva={manejarSeleccionMasiva}
          aprobarCotizacion={aprobarCotizacion}
          aprobarRechazarCotizaciones={aprobarRechazarCotizaciones}
          rechazarCotizacion={rechazarCotizacion}
        />
      </ProveedorCargando>
    </>
  )
}

export default CotizadorCV;
